import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { School, Check, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { API } from "../App";

/**
 * Dialog for an authenticated student WITHOUT a school to pick one and
 * submit a join request. Calls onSubmitted() after a successful POST so
 * the parent can refresh the user state.
 */
export default function SelectSchoolDialog({ open, onOpenChange, onSubmitted }) {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPicked(null);
    setLoading(true);
    axios
      .get(`${API}/schools/public`, { withCredentials: true })
      .then((r) => setSchools(r.data || []))
      .catch(() => toast.error("Erro ao carregar escolas"))
      .finally(() => setLoading(false));
  }, [open]);

  const submit = async () => {
    if (!picked) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/me/request-school`,
        { school_id: picked.school_id },
        { withCredentials: true },
      );
      toast.success(`Pedido enviado para ${picked.name}`);
      onSubmitted?.();
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar pedido");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-950 border-slate-800 max-w-lg" data-testid="select-school-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <School className="w-4 h-4 text-orange-400" />
            Selecionar Escola
          </DialogTitle>
          <DialogDescription>
            Escolhe a tua escola. Um administrador da escola irá rever o teu pedido e atribuir-te a uma turma.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> A carregar escolas…
          </div>
        ) : schools.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-sm">
            Sem escolas disponíveis de momento.
          </div>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {schools.map((s) => {
              const isPicked = picked?.school_id === s.school_id;
              return (
                <button
                  key={s.school_id}
                  onClick={() => setPicked(s)}
                  data-testid={`pick-school-${s.school_id}`}
                  className={`w-full text-left flex items-center gap-3 px-3 py-3 rounded-lg border transition-all
                    ${isPicked
                      ? "border-orange-500/60 bg-orange-500/10"
                      : "border-slate-800 bg-slate-900/40 hover:border-slate-700 hover:bg-slate-900/70"}`}
                >
                  {s.logo_url ? (
                    <img
                      src={s.logo_url}
                      alt={s.name}
                      className="w-10 h-10 rounded object-contain border border-slate-800 bg-slate-900 p-0.5"
                    />
                  ) : (
                    <div
                      className="w-10 h-10 rounded flex items-center justify-center"
                      style={{ background: s.primary_color || "#f97316" }}
                    >
                      <School className="w-5 h-5 text-white" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-cockpit-text-bright truncate">{s.name}</div>
                    <div className="text-[11px] text-slate-500 font-mono truncate">{s.slug}</div>
                  </div>
                  {isPicked && <Check className="w-4 h-4 text-orange-400" />}
                </button>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            className="btn-secondary-cockpit"
            onClick={() => onOpenChange?.(false)}
          >
            Cancelar
          </Button>
          <Button
            className="btn-cockpit"
            disabled={!picked || submitting}
            onClick={submit}
            data-testid="submit-school-request"
          >
            {submitting ? "A enviar…" : "Enviar pedido"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
